import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { Bell, LayoutDashboard, ClipboardList, SearchCheck, BookOpen, FileText, FolderLock, ClipboardCheck, AlertTriangle, Users2, Wrench, UserCheck,
  ShieldAlert, LineChart, FileBarChart, Building2, Settings, LogOut, Menu, ListTodo, KeyRound, CreditCard, Lock } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { ProductMark, IsoCred } from "@/components/Brand";
import { useAuth, can } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ThemeToggle } from "@/context/ThemeContext";

const NAV = [
  ["Overview", [["/dashboard", "Dashboard", LayoutDashboard], ["/tasks", "Tasks", ListTodo]]],
  ["Accreditation", [["/tracker", "Master Tracker", ClipboardList], ["/gap-analysis", "Gap Analysis", SearchCheck], ["/reference", "NABL Reference", BookOpen]]],
  ["Documents & Evidence", [["/documents", "Document Studio", FileText], ["/evidence", "Evidence Repository", FolderLock]]],
  ["Quality Operations", [["/audits", "Internal Audits", ClipboardCheck], ["/capa", "NC & CAPA", AlertTriangle], ["/reviews", "Management Review", Users2]]],
  ["Resources", [["/equipment", "Equipment", Wrench], ["/personnel", "Personnel", UserCheck, "view_personnel"], ["/risks", "Risk Register", ShieldAlert], ["/indicators", "Quality Indicators", LineChart]]],
  ["Workspace", [["/reports", "Reports & Exports", FileBarChart], ["/profile", "Lab Profile", Building2], ["/team", "Team & Access", KeyRound], ["/billing", "Billing & Plans", CreditCard], ["/settings", "Settings", Settings]]],
];
const GATED = { "/audits": "capa_audit", "/capa": "capa_audit", "/reviews": "capa_audit", "/equipment": "resources", "/personnel": "resources", "/risks": "advanced_dash", "/indicators": "advanced_dash" };
const locked = (user, to) => GATED[to] && user?.plan && !user.plan.features.includes(GATED[to]);

function PlanBanner({ plan }) {
  if (!plan || plan.state === "active") return null;
  const exp = plan.state === "expired";
  const left = exp ? 0 : plan.trial_limits.ai - plan.trial_usage.ai;
  return (
    <div className={`px-4 lg:px-8 py-2 text-[13px] flex flex-wrap gap-2 items-center ${exp ? "bg-red-50 text-[#B23A3A]" : "bg-[#F3EFFA] text-[#48217A]"}`} data-testid="plan-banner">
      {exp ? "Your plan has expired. Your data is safe — choose a plan to keep working." : `Free trial: try every feature with limited usage (${left} AI generation${left === 1 ? "" : "s"} left). Subscribe for full access.`}
      <Link to="/billing" className="font-semibold underline" data-testid="plan-banner-link">{exp ? "View plans" : "See trial usage & plans"}</Link>
    </div>
  );
}

const SideNav = ({ user, onNav }) => (
  <nav className="flex flex-col gap-5 px-3 pb-6">
    {NAV.map(([group, items]) => (
      <div key={group}>
        <div className="px-3 mb-1.5 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#5A6B87]/70">{group}</div>
        {items.filter((i) => !i[3] || can(user, i[3])).map(([to, label, Icon]) => (
          <NavLink key={to} to={to} onClick={onNav} data-testid={`nav-${to.slice(1)}`}
            className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-md text-[13.5px] transition-colors border-l-[3px] ${isActive ? "bg-[#F3EFFA] text-[#48217A] font-semibold border-[#48217A]" : "border-transparent text-[#3B4A63] hover:bg-slate-50"}`}>
            <Icon className="h-4 w-4" />{label}{locked(user, to) && <Lock className="h-3 w-3 ml-auto text-[#5A6B87]" data-testid={`nav-lock-${to.slice(1)}`} />}
          </NavLink>
        ))}
      </div>
    ))}
  </nav>
);

function Notifications() {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  return (
    <Popover onOpenChange={(o) => { if (!o && data.unread) api.post("/notifications/read-all").then(load); }}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-md hover:bg-slate-100" data-testid="notifications-bell">
          <Bell className="h-5 w-5 text-[#3B4A63]" />
          {data.unread > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#D64545] text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center" data-testid="notifications-count">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-4 py-3 border-b font-semibold text-sm">Notifications</div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <div className="p-6 text-sm text-center text-[#5A6B87]">You're all caught up.</div>}
          {data.items.map((n) => (
            <button key={n.id} onClick={() => n.link && nav(n.link)} className={`w-full text-left px-4 py-2.5 border-b hover:bg-slate-50 ${n.read ? "" : "bg-[#F3EFFA]/60"}`}>
              <div className="text-[13px] font-medium">{n.title}</div>
              <div className="text-xs text-[#5A6B87] line-clamp-2">{n.body}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const out = async () => { await logout(); nav("/login"); };
  return (
    <div className="min-h-screen bg-[#F4F6F8] flex">
      <aside className="hidden lg:flex flex-col w-[248px] shrink-0 bg-white border-r h-screen sticky top-0 overflow-y-auto">
        <Link to="/dashboard" className="px-5 py-5"><ProductMark /></Link>
        <SideNav user={user} />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[270px] p-0 overflow-y-auto">
          <div className="px-5 py-5"><ProductMark /></div>
          <SideNav user={user} onNav={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 h-14 bg-white/90 backdrop-blur-md border-b flex items-center px-4 lg:px-8 gap-3">
          <button className="lg:hidden p-2 -ml-2" onClick={() => setOpen(true)} data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></button>
          <div className="lg:hidden"><ProductMark compact /></div>
          <div className="hidden md:block text-sm font-medium text-[#172238] truncate" data-testid="header-lab-name">{user?.lab_name}</div>
          <div className="ml-auto flex items-center gap-2">
            <Notifications />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-md hover:bg-slate-100" data-testid="user-menu-button">
                  <div className="h-8 w-8 rounded-full bg-[#48217A] text-white flex items-center justify-center text-sm font-semibold">{user?.name?.[0]}</div>
                  <div className="hidden sm:block text-left leading-tight"><div className="text-[13px] font-medium">{user?.name}</div><div className="text-[11px] text-[#5A6B87]">{user?.role_label}</div></div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs font-normal text-[#5A6B87]">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/change-password")} data-testid="menu-change-password">Change password</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/settings")}>Notification preferences</DropdownMenuItem>
                <div className="flex items-center justify-between px-2 py-1.5 text-sm"><span>Dark mode</span><ThemeToggle testid="menu-theme-toggle" /></div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={out} className="text-[#D64545]" data-testid="logout-button"><LogOut className="h-4 w-4 mr-2" />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <PlanBanner plan={user?.plan} />
        <main className="flex-1 p-4 lg:p-8 min-w-0 overflow-x-hidden"><Outlet /></main>
        <footer className="px-8 py-4 text-[11px] text-[#5A6B87] border-t bg-white flex flex-wrap gap-3 justify-between">
          <span><IsoCred /> by Nirogyam · Internal readiness indicators — not an official NABL decision.</span>
          <span className="flex gap-3"><Link to="/legal/terms">Terms</Link><Link to="/legal/privacy">Privacy</Link></span>
        </footer>
      </div>
    </div>
  );
}
